import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  AXIOS_INSTANCE,
  createUserPermission,
  fetchUserPermissionReason,
  updateGrantedPermissionToUser,
  useFetchUserPermissions} from '@jobandtalent/auth-api-client-v2';
import {
  FetchPermissions200,
  FetchPermissions200DataPermissionsItem
} from '@jobandtalent/auth-api-client-v2/dist/apiClient/model';
import { Select, Textarea } from '@jobandtalent/design-system';

import useSWRInfinite from 'swr/infinite';

import { formatDataScopesData, formatDataScopesInput } from 'src/common/dataScopesHelper';
import FormInput from 'src/components/FormInput/FormInput';
import SavedReason from 'src/components/SavedReason/SavedReason';
import StackView, { SaveCancelFooter } from 'src/components/StackView/StackView';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';

import BulkAssignmentSelect from '../BulkAssignments/BulkAssignmentSelect';
import useBulkAssignments from '../BulkAssignments/useBulkAssignments';
import ReasonField, { optionalReasonRules, reasonRules } from '../ReasonField/ReasonField';
import { PermissionFormProps, PermissionOption, TextareaValue } from '../Users.types';

const formatContent = (content: string): string => {
  return content.replace(/, */g, ',\n');
};

const calculateHeight = (displayValue: string, defaultHeight: string): string => {
  const textarea = document.createElement('textarea');
  textarea.style.visibility = 'hidden';
  textarea.style.position = 'absolute';
  textarea.style.height = 'auto';
  textarea.style.whiteSpace = 'pre';
  textarea.value = displayValue;

  document.body.appendChild(textarea);
  const calculatedHeight = Math.max(textarea.scrollHeight, parseInt(defaultHeight));
  document.body.removeChild(textarea);

  return `${calculatedHeight}px`;
};

const fetcher = (input: RequestInfo): Promise<FetchPermissions200> => {
  return AXIOS_INSTANCE({ url: input as string, method: 'GET' }).then((res) => res.data);
};

const getKey = (pageIndex: number, previousPageData: FetchPermissions200) => {
  // reached the end
  if (previousPageData && !previousPageData.meta.next_page === null) {
    return null;
  }

  return `/permissions?page=${pageIndex + 1}&per_page=100`; // SWR key
};

const PermissionForm = ({
  onClose,
  onSuccess,
  selectedUser,
  selectedPermission,
  bulk = false,
  onAssignmentsChanged
}: PermissionFormProps) => {
  const {
    data: permissionsData,
    error: permissionsError,
    size,
    setSize
  } = useSWRInfinite<FetchPermissions200>(getKey, fetcher, {
    revalidateFirstPage: false
  });
  const { error, setError } = useError();
  const bulkAssignments = useBulkAssignments();
  const { data: assignedPermissions, error: assignedPermissionsError } = useFetchUserPermissions(selectedUser.id, {
    swr: { enabled: bulk }
  });
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { control, handleSubmit } = useForm<{ permissionId: PermissionOption; reason: string; resourceIds: TextareaValue; dataScopes: TextareaValue }>();
  const { permissions, error: permissionError } = useLocalPermissions();
  const companyAdminPermission = !permissionError && permissions.companyAdminPermission;
  const serviceUsersPermission = !permissionError && permissions.serviceUsersPermission;
  const canManageUsersPermissions = !permissionError && permissions.manageUsersPermissionsPermission;
  const isViewOnly = !!selectedPermission && !canManageUsersPermissions;

  useEffect(() => {
    if (permissionsData) {
      // if there's still something to fetch, fetch it before setting select options
      if (permissionsData.at(-1)?.meta.next_page !== null) {
        setSize(size + 1);
      } else {
        const permissions = permissionsData
        .reduce(
          (acc: FetchPermissions200DataPermissionsItem[], permissionsPage) => [
            ...acc,
            ...permissionsPage.data.permissions
          ],
          []
        );

        const isS2sUser = selectedUser.service_user?.account_type === 'service_to_service';
        const providerInternalServiceId = selectedUser.service_user?.provider_internal_service_id;
        const hasProviderInternalService = providerInternalServiceId != null;

        const permissionsForUser =
          isS2sUser && hasProviderInternalService
            ? permissions.filter(
                ({ internal_service }) => internal_service?.id === providerInternalServiceId
              )
            : permissions;

        const data = permissionsForUser
          .filter(
            ({ id, assignment_restricted }) =>
              selectedPermission?.id === id || !assignment_restricted
          )
          .map(({ id, name, scope_name, app, allowed_user_types }) => ({
            value: id,
            label: app ? `${scope_name}:${name} (Company App)` : `${scope_name}:${name}`,
            app: app,
            allowed_user_types: allowed_user_types
          }));

        let filteredOptions = data;

        // Filter for users WITHOUT companyAdminPermission: remove permissions with app
        if (!companyAdminPermission) {
          filteredOptions = filteredOptions.filter(option => !option.app);
        }

        const isCompanyUser = selectedUser.company_users && selectedUser.company_users.length > 0;
        if (!isCompanyUser) {
          filteredOptions = filteredOptions.filter(option => !option.app);
        }

        // Filter based on the user's account_type (for service users)
        // List of restricted user types that have specific permission requirements
        const RESTRICTED_USER_TYPES = ['saas'];
        const userAccountType = selectedUser.service_user?.account_type;

        if (userAccountType && RESTRICTED_USER_TYPES.includes(userAccountType)) {
          // For restricted user types (like SaaS), ONLY show permissions that explicitly include the account_type
          filteredOptions = filteredOptions.filter(option => {
            // Only include if allowed_user_types contains the user's account_type
            return Array.isArray(option.allowed_user_types) && option.allowed_user_types.includes(userAccountType);
          });
        } else {
          // For non-restricted service users (client, bot, etc.) and standard users
          // Exclude permissions that are ONLY for restricted types
          filteredOptions = filteredOptions.filter(option => {
            if (!option.allowed_user_types || option.allowed_user_types.length === 0) {
              return true; // Generic permissions are OK
            }
            // Exclude permissions that ONLY have restricted types
            const hasOnlyRestrictedTypes = option.allowed_user_types.every(type =>
              RESTRICTED_USER_TYPES.includes(type)
            );
            return !hasOnlyRestrictedTypes;
          });

          // Filter based on serviceUsersPermission (for standard users)
          if (!serviceUsersPermission) {
            // Users WITHOUT serviceUsersPermission: remove permissions with allowed_user_types unless they include 'standard_user'
            filteredOptions = filteredOptions.filter(option => {
              // If no allowed_user_types, include the permission
              if (!option.allowed_user_types || option.allowed_user_types.length === 0) {
                return true;
              }
              // If allowed_user_types exists, only include if it contains 'standard_user'
              return option.allowed_user_types.includes('standard_user');
            });
          }
          // Users WITH serviceUsersPermission see all permissions (no additional filtering)
        }

        setPermissionOptions(filteredOptions);
      }
    }
  }, [permissionsData, companyAdminPermission, serviceUsersPermission, selectedUser, selectedPermission]);

  useEffect(() => {
    if (permissionsError || assignedPermissionsError) {
      setError(permissionsError || assignedPermissionsError);
    }
  }, [permissionsError, assignedPermissionsError]);

  const onSubmit = async (data: { permissionId: PermissionOption; reason: string; resourceIds: TextareaValue; dataScopes: TextareaValue; }) => {
    if (bulk && !selectedPermission) {
      const shared = {
        reason: data.reason.trim(),
        resource_ids: data.resourceIds?.value || '',
        data_scope: formatDataScopesData(dataScopeValue)
      };
      const complete = await bulkAssignments.submit((option) => createUserPermission(selectedUser.id, {
        user_permission: { ...shared, permission_id: option.value }
      }), onAssignmentsChanged);
      if (complete) onSuccess();
      return;
    }
    setIsSaving(true);
    if (selectedPermission) {
      updateGrantedPermissionToUser(selectedUser.id, selectedPermission.id, {
        user_permission: {
          ...userPermissionsData(data),
          ...(data.reason.trim() ? { reason: data.reason.trim() } : {})
        }
      }).then(onSuccess, (error) => {
        setIsSaving(false);
        setError(error?.response?.data?.message ? error?.response?.data : DEFAULT_ERROR);
      });
    } else {
      createUserPermission(selectedUser.id, {
        user_permission: { ...userPermissionsData(data), reason: data.reason.trim() }
      }).then(onSuccess, (error) => {
        setIsSaving(false);
        setError(error?.response?.data?.message ? error?.response?.data : DEFAULT_ERROR);
      });
    }
  };

  type PermissionFormDataTypeWithoutDataScopes = {
    permission_id: number;
    resource_ids: string;
  };

  type PermissionFormDataTypeWithDataScopes = {
    permission_id: number;
    resource_ids: string;
    data_scope: string[];
  };

  const userPermissionsData = (data: { permissionId: PermissionOption; reason: string; resourceIds: TextareaValue; dataScopes: TextareaValue; }): PermissionFormDataTypeWithDataScopes | PermissionFormDataTypeWithoutDataScopes => {
    return {
      permission_id: selectedPermission?.id ?? data.permissionId.value,
      resource_ids: data.resourceIds?.value ?? selectedPermission?.resource_ids ?? '',
      data_scope: formatDataScopesData(dataScopeValue)
    }

    return {
      permission_id: selectedPermission?.id ?? data.permissionId.value,
      resource_ids: data.resourceIds?.value ?? selectedPermission?.resource_ids ?? ''
    }
  }
  
  interface KeyValuePair {
    key: string;
    value: string;
  }

  const dataScope = selectedPermission ? formatDataScopesInput(selectedPermission.data_scope) : '';

  const [dataPairs, setDataPairs] = useState<KeyValuePair[]>([]);
  const [dataScopeValue, setDataScopeValue] = useState<string>(dataScope);

  useEffect(() => {
    const parsedData = parseDataScopesInput(dataScope);
    setDataPairs(parsedData);
  }, [dataScope]);

  const [shouldRenderDataPairs, setShouldRenderDataPairs] = useState(false);

  const parseDataScopesInput = (input: string): KeyValuePair[] => {
    return input.split(',').map(pair => {
      const [key, value] = pair.split(':').map(s => s.trim());
      return { key, value };
    });
  };

  const handleTextareaChange = (value: string) => {
    const newValue = value;
    setDataScopeValue(formatDataScopesInput(formatDataScopesData(value)));
    const newData = parseDataScopesInput(newValue);
    setDataPairs(newData);
  };

  const handlePairChange = (index: number, keyOrValue: 'key' | 'value', newValue: string) => {
    const newData = dataPairs.map((pair, i) => {
      if (i === index) {
        return { ...pair, [keyOrValue]: newValue };
      }
      return pair;
    });
    setDataPairs(newData);
    setDataScopeValue(newData.map(pair => `${pair.key}:${pair.value}`).join(','));
  };

  const closeForm = () => {
    if (!bulkAssignments.isSaving) onClose();
  };

  const StackViewFooter = <SaveCancelFooter cancelLabel={isViewOnly ? 'Close' : 'Cancel'} hideSave={isViewOnly} isCancelDisabled={bulkAssignments.isSaving} isSaveDisabled={isSaving || (bulk && (bulkAssignments.isSaving || !bulkAssignments.selected.length))} onClose={closeForm} onSelect={handleSubmit(onSubmit)} />;
  const keyValuePattern = /\w+:\w+/;
  return (
    <StackView
      footer={StackViewFooter}
      onClose={closeForm}
      title={isViewOnly ? 'View permission' : selectedPermission ? 'Edit permission' : bulk ? 'Bulk add permissions' : 'Grant new permission'}
      nested={true}
    >
      <FormInput label={bulk ? "Permissions" : "Permission name"}>
        {bulk && !selectedPermission ? (
          <BulkAssignmentSelect
            shareResourceIds
            options={assignedPermissions ? permissionOptions.filter((option) =>
              !assignedPermissions.data.user_permissions.some((permission) => permission.id === option.value)
            ) : []}
            assignments={bulkAssignments}
          />
        ) : permissionOptions.length ? (
          <Controller
            name="permissionId"
            control={control}
            defaultValue={permissionOptions.find((option) => option.value === selectedPermission?.id)}
            render={({ field: { onChange } }) => (
              <Select
                onChange={onChange}
                options={permissionOptions}
                isDisabled={!!selectedPermission || isViewOnly}
                defaultValue={
                  selectedPermission
                    ? permissionOptions.find((option) => option.value === selectedPermission.id)
                    : undefined
                }
              />
            )}
          />
        ) : null}
      </FormInput>
      <Controller
        name="resourceIds"
        control={control}
        render={({ field: { onChange } }) => (
          <Textarea
            height={87}
            disabled={isViewOnly}
            label="Resource IDs"
            onChange={onChange}
            value={selectedPermission ? selectedPermission.resource_ids : ''}
            placeholder="Resource IDs must be separated by commas. i.e., value1, value2, value3"
          />
        )}
      />

      <Controller
        name="dataScopes"
        control={control}
        render={({ field: { onChange } }) => (
          <div>
            <label htmlFor="data" className="ds-text-body-base-semibold">Data Scopes</label>
            {shouldRenderDataPairs && dataPairs.map((pair, index) => (
              <div className="data-scope-block" key={index}>
                <input
                  name="data"
                  type="text"
                  value={pair.key}
                  onChange={(e) => handlePairChange(index, 'key', e.target.value)}
                  disabled={isViewOnly || !!(selectedPermission?.app && !companyAdminPermission)}
                />
                <input
                  type="text"
                  value={pair.value}
                  onChange={(e) => handlePairChange(index, 'value', e.target.value)}
                  disabled={isViewOnly || !!(selectedPermission?.app && !companyAdminPermission)}
                />
              </div>
            ))}
            <Textarea
              name="data-scope"
              height={calculateHeight(formatContent(dataScope), "87")}
              onChange={(e) => {
                handleTextareaChange(e.value);
                onChange(e);
                if (keyValuePattern.test(e.value)) {
                  setShouldRenderDataPairs(true);
                } else {
                  setShouldRenderDataPairs(false);
                }
              }}
              value={formatContent(dataScopeValue)}
              placeholder="Data scopes must be separated by commas. i.e., company_id:1, company_id:2, country:ES"
              disabled={isViewOnly || !!(selectedPermission?.app && !companyAdminPermission)}
            />
          </div>
        )}
      />

      {selectedPermission && <SavedReason canReadReason={!permissionError && permissions.readAssignmentDetailsPermission} loadReason={() => fetchUserPermissionReason(selectedUser.id, selectedPermission.id)} />}
      <Controller
        name="reason"
        control={control}
        defaultValue=""
        rules={selectedPermission ? optionalReasonRules : reasonRules}
        render={({ field, fieldState }) => (
          <ReasonField
            value={field.value}
            onChange={field.onChange}
            inputRef={field.ref}
            error={fieldState.error}
            append={!!selectedPermission}
            disabled={isViewOnly}
            placeholder={selectedPermission ? "Write an additional reason to append" : "Write why this user needs this permission"}
          />
        )}
      />
      {error}
    </StackView>
  );
};

export default PermissionForm;
